/**
 * Bench scorecard — the screen that answers "what is giving better results".
 *
 * Two things it refuses to do, both deliberate:
 *
 *  - **It never blends extraction and analysis.** They're separate tables,
 *    because "which vision model" and "which reasoning model" are different
 *    questions with different answers.
 *  - **It never presents a judge number without its agreement.** A judge that
 *    disagrees with you is not evidence, and a leaderboard that hides that is
 *    worse than no leaderboard.
 *
 * See docs/FeatureDesigns/Evals/assessment-eval-bench.md §7.3, §6.3.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { CellStats, AgreementStats, StepRole } from '../../types/scenarioEvals';
import './BenchTab.css';

const ROLE_ORDER: StepRole[] = ['reading', 'assessment', 'combined', 'scoring'];
const ROLE_LABEL: Record<string, string> = {
    reading: 'Reading — which model transcribes the work best',
    assessment: 'Assessment — which model judges the work best',
    combined: 'Combined (single-step) — reads and judges in one call',
    scoring: 'Scoring — deterministic step',
};

export function BenchScorecard({ datasetId }: { datasetId: string }) {
    const qc = useQueryClient();
    const [evaluator, setEvaluator] = useState<'human' | 'judge'>('human');

    const { data: card, isLoading } = useQuery({
        queryKey: ['benchScorecard', datasetId, evaluator],
        queryFn: () => adminApi.getBenchScorecard(datasetId, evaluator),
    });
    // The other side too, so the toggle can say what is behind it and the view
    // can land on whichever side has anything. Same endpoint, cached per
    // evaluator — switching then costs nothing.
    const otherEvaluator = evaluator === 'human' ? 'judge' : 'human';
    const { data: otherCard } = useQuery({
        queryKey: ['benchScorecard', datasetId, otherEvaluator],
        queryFn: () => adminApi.getBenchScorecard(datasetId, otherEvaluator),
    });
    const { data: agreement } = useQuery({
        queryKey: ['benchAgreement', datasetId],
        queryFn: () => adminApi.getBenchAgreement(datasetId),
    });

    // On by default because a bench built from hand-picked production
    // submissions has nothing else in it — every cell is historical until you
    // launch a bench run of your own.
    const [includeHistorical, setIncludeHistorical] = useState(true);

    const judge = useMutation({
        mutationFn: () => adminApi.judgeBench(datasetId, { includeHistorical }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['benchScorecard', datasetId] });
            qc.invalidateQueries({ queryKey: ['benchAgreement', datasetId] });
            qc.invalidateQueries({ queryKey: ['benchMatrix', datasetId] });
        },
    });

    // Land on the side that has something to show, once. Guarded so a reader
    // who deliberately switches to an empty side is not bounced straight back.
    const autoSwitched = useRef(false);
    useEffect(() => {
        if (autoSwitched.current || !card || !otherCard) return;
        autoSwitched.current = true;
        if (card.totals.verdicts === 0 && otherCard.totals.verdicts > 0) setEvaluator(otherEvaluator);
    }, [card, otherCard, otherEvaluator]);

    if (isLoading) return <p className="bench-muted">Loading scorecard…</p>;
    if (!card) return null;

    const roles = ROLE_ORDER.filter(r => card.byRole[r]?.length);
    const overall = agreement?.overall;
    /** What the evaluator you are NOT looking at has to say. */
    const other = otherCard?.totals.verdicts ?? 0;

    return (
        <div className="bench">
            <div className="bench-toolbar">
                <div className="bench-counts">
                    <span className="bench-stat"><strong>{card.totals.cells}</strong> cells</span>
                    <span className="bench-stat"><strong>{card.totals.verdicts}</strong> verdicts</span>
                    <span className="bench-stat"><strong>{card.totals.rowsJudged}</strong> rows judged</span>
                </div>
                <div className="bench-actions">
                    {/* Counts on the toggle: which side has anything on it is
                        the first thing to know, and it is invisible from the
                        side you happen to be standing on. */}
                    <div className="bench-seg">
                        <button className={evaluator === 'human' ? 'on' : ''} onClick={() => setEvaluator('human')}>
                            Your verdicts ({evaluator === 'human' ? card.totals.verdicts : other})
                        </button>
                        <button className={evaluator === 'judge' ? 'on' : ''} onClick={() => setEvaluator('judge')}>
                            Judge ({evaluator === 'judge' ? card.totals.verdicts : other})
                        </button>
                    </div>
                    <label className="bench-check"
                        title="Cells produced by the shipped pipeline rather than a bench run. They came from whatever prompt version was live at the time; their verdicts get their own scorecard rows rather than being pooled with replayed ones.">
                        <input type="checkbox" checked={includeHistorical}
                            onChange={e => setIncludeHistorical(e.target.checked)} />
                        Include historical
                    </label>
                    <button className="btn btn-secondary" disabled={judge.isPending} onClick={() => judge.mutate()}>
                        {judge.isPending ? 'Judging…' : 'Run judge on unjudged cells'}
                    </button>
                </div>
            </div>

            {judge.data && (
                <p className="bench-muted">
                    Judged {judge.data.cellsJudged} of {judge.data.cellsConsidered} cells
                    across {judge.data.batches} batch{judge.data.batches === 1 ? '' : 'es'}
                    {judge.data.failedBatches > 0 && ` — ${judge.data.failedBatches} batch(es) failed`}.
                </p>
            )}
            {judge.error && <p className="bench-error">{String(judge.error)}</p>}

            {evaluator === 'judge' && <AgreementBanner overall={overall} byRole={agreement?.byRole} />}

            {!!card.modelsSpanningPromptVersions.length && (
                <p className="bench-warnbar">
                    Measured under more than one prompt version — not readable as a single number:{' '}
                    <strong>{card.modelsSpanningPromptVersions.join(', ')}</strong>
                </p>
            )}

            {!roles.length && (
                <p className="bench-empty">
                    {other
                        ? <>
                            Nothing from {evaluator === 'human' ? 'you' : 'the judge'} yet — but{' '}
                            <button type="button" className="bench-linkbtn"
                                onClick={() => setEvaluator(evaluator === 'human' ? 'judge' : 'human')}>
                                {evaluator === 'human' ? `the judge has ${other}` : `you have ${other}`}
                            </button>.
                        </>
                        : <>No verdicts yet. Judge cells on the Solutions tab, or run the judge above.</>}
                </p>
            )}

            {roles.map(role => (
                <section key={role} className="bench-board">
                    <h3>{ROLE_LABEL[role] ?? role}</h3>
                    <LeaderboardTable rows={card.byRole[role]} role={role} />
                </section>
            ))}
        </div>
    );
}

/**
 * The judge's numbers are only meaningful next to this. κ below ~0.4 means its
 * verdicts disagree with yours often enough that the leaderboard above is not
 * evidence — fix the judge prompt before reading it.
 */
function AgreementBanner({ overall, byRole }: { overall?: AgreementStats; byRole?: Record<string, AgreementStats> }) {
    if (!overall || !overall.n) {
        return (
            <p className="bench-warnbar">
                No cell has both a human and a judge verdict yet, so there is nothing calibrating these
                numbers. Judge a handful by hand first — the judge is only as trustworthy as its agreement with you.
            </p>
        );
    }
    const k = overall.kappa;
    const tone = k === undefined ? 'warn' : k >= 0.6 ? 'ok' : k >= 0.4 ? 'warn' : 'bad';
    return (
        <div className={`bench-agree ${tone}`}>
            <div>
                <strong>Agreement with you</strong> over {overall.n} doubly-judged cell{overall.n === 1 ? '' : 's'}:{' '}
                {k !== undefined ? `κ ${k.toFixed(2)}` : 'κ n/a'}, raw {(overall.rawAgreement * 100).toFixed(0)}%
                {overall.judgeLenient > 0 && (
                    <> — <strong>{overall.judgeLenient}</strong> case{overall.judgeLenient === 1 ? '' : 's'} you
                    called bad and the judge waved through</>
                )}
            </div>
            {byRole && (
                <div className="bench-agree-roles">
                    {Object.entries(byRole).map(([role, s]) => (
                        <span key={role} className="bench-pill">
                            {role}: κ {s.kappa !== undefined ? s.kappa.toFixed(2) : '—'} (n={s.n})
                        </span>
                    ))}
                </div>
            )}
            {tone === 'bad' && (
                <div className="bench-muted">
                    Below κ 0.4 the judge is not tracking your judgement. Treat its verdicts as unverified
                    and work on the judge prompt.
                </div>
            )}
        </div>
    );
}

function LeaderboardTable({ rows, role }: { rows: CellStats[]; role: StepRole }) {
    const showConditional = role === 'assessment' || role === 'scoring';
    return (
        <div className="bench-tablewrap">
            <table className="bench-table">
                <thead>
                    <tr>
                        <th>Model</th>
                        <th title="Whether these verdicts judged cells the bench replayed, or the output production actually shipped. The same model appears once per source: averaging the two answers neither question.">
                            Source
                        </th>
                        <th>Prompt</th>
                        <th title="(good + acceptable/2) / n">Quality</th>
                        <th>n</th>
                        <th>good / acc / bad</th>
                        {showConditional && (
                            <th title="Quality given the transcription this step was handed. A large positive gap means the reading step is the problem, not this model.">
                                Given input
                            </th>
                        )}
                        <th title="Share of rows where you ranked this cell first">Win rate</th>
                        <th>p50 / p95</th>
                        <th>Cost</th>
                        <th>Top failures</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(c => (
                        <tr key={`${c.cellKey}::${c.source}`}>
                            <td><strong>{c.provider ?? '—'}</strong></td>
                            <td className="bench-muted">{c.source === 'auto' ? 'shipped' : 'bench'}</td>
                            <td className="bench-muted">
                                {c.promptVersions.length ? `v${c.promptVersions.join(', v')}` : '—'}
                            </td>
                            <td><QualityBar value={c.quality} /></td>
                            <td>{c.n}</td>
                            <td className="bench-muted">{c.good} / {c.acceptable} / {c.bad}</td>
                            {showConditional && (
                                <td>
                                    {c.conditionalQuality !== undefined
                                        ? <>
                                            {(c.conditionalQuality * 100).toFixed(0)}%
                                            {!!c.upstreamPenalty && c.upstreamPenalty > 0.1 && (
                                                <span className="bench-upstream" title="Reasoned correctly on what it received — the failure came from upstream.">
                                                    ↑{(c.upstreamPenalty * 100).toFixed(0)}
                                                </span>
                                            )}
                                          </>
                                        : <span className="bench-muted">—</span>}
                                </td>
                            )}
                            <td>{c.winRate !== undefined ? `${(c.winRate * 100).toFixed(0)}% (${c.ranked})` : <span className="bench-muted">—</span>}</td>
                            <td className="bench-muted">
                                {c.latencyMsP50 !== undefined ? `${Math.round(c.latencyMsP50)} / ${Math.round(c.latencyMsP95 ?? 0)}ms` : '—'}
                            </td>
                            <td className="bench-muted">{c.costUsdMean !== undefined ? `$${c.costUsdMean.toFixed(4)}` : '—'}</td>
                            <td>
                                {Object.entries(c.failureTags)
                                    .sort((a, b) => b[1] - a[1]).slice(0, 3)
                                    .map(([tag, n]) => <span key={tag} className="bench-tag">{tag} {n}</span>)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function QualityBar({ value }: { value: number }) {
    const pct = Math.round(value * 100);
    const tone = value >= 0.8 ? 'ok' : value >= 0.5 ? 'warn' : 'bad';
    return (
        <span className="bench-qbar" title={`${pct}%`}>
            <span className={`bench-qbar-fill ${tone}`} style={{ width: `${pct}%` }} />
            <span className="bench-qbar-label">{pct}%</span>
        </span>
    );
}

export default BenchScorecard;
